import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import {
  ApiError,
  api,
  clearTokens,
  getAccessToken,
  setUnauthorizedHandler,
  setTokens,
  type Tokens,
  type UiTheme,
  type User,
} from '../api/client';
import { clearStoredTableStates } from '../lib/tableState';

type AuthState = {
  user: User | null;
  ready: boolean;
  login: (username: string, password: string) => Promise<void>;
  logout: () => void;
  refreshUser: () => Promise<User | null>;
};

const AuthCtx = createContext<AuthState | null>(null);

export function applyUserTheme(theme: UiTheme): void {
  if (typeof document === 'undefined') return;
  document.documentElement.classList.toggle('dark', theme === 'dark');
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [ready, setReady] = useState(false);

  const handleUnauthorized = useCallback(() => {
    clearTokens();
    clearStoredTableStates();
    applyUserTheme('dark');
    setUser(null);
    if (typeof window !== 'undefined' && window.location.pathname !== '/login') {
      window.location.assign('/login');
    }
  }, []);

  useEffect(() => {
    setUnauthorizedHandler(handleUnauthorized);
    return () => setUnauthorizedHandler(null);
  }, [handleUnauthorized]);

  const refreshUser = useCallback(async () => {
    try {
      const me = await api.get<User>('/api/auth/me');
      applyUserTheme(me.ui_theme);
      setUser(me);
      return me;
    } catch (err) {
      clearTokens();
      setUser(null);
      if (err instanceof ApiError) throw err;
      throw err;
    }
  }, []);

  // Восстанавливаем сессию по access-токену из sessionStorage (после F5).
  useEffect(() => {
    const token = getAccessToken();
    if (!token) {
      setReady(true);
      return;
    }
    let cancelled = false;
    api
      .get<User>('/api/auth/me')
      .then((u) => {
        if (!cancelled) setUser(u);
      })
      .catch(() => {
        if (!cancelled) {
          handleUnauthorized();
        }
      })
      .finally(() => {
        if (!cancelled) setReady(true);
      });
    return () => {
      cancelled = true;
    };
  }, [handleUnauthorized]);

  useEffect(() => {
    applyUserTheme(user?.ui_theme ?? 'dark');
  }, [user?.ui_theme]);

  const value = useMemo<AuthState>(
    () => ({
      user,
      ready,
      login: async (username, password) => {
        const tokens = await api.postPublic<Tokens>('/api/auth/login', {
          username,
          password,
        });
        setTokens(tokens);
        try {
          await refreshUser();
        } catch (err) {
          clearTokens();
          setUser(null);
          if (err instanceof ApiError) throw err;
          throw err;
        }
      },
      logout: () => {
        clearTokens();
        clearStoredTableStates();
        applyUserTheme('dark');
        setUser(null);
      },
      refreshUser,
    }),
    [user, ready, refreshUser],
  );

  return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthCtx);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}

export function useOptionalAuth(): AuthState | null {
  return useContext(AuthCtx);
}
